import React from "react";

import isIntuit from "Intuit/isIntuit";
import { useSelector } from "react-redux";
import formatCurrency from "Utils/formatCurrency";
import simplifyDisabilityPlanName from "Utils/simplifyDisabilityPlanName";

import { Body1, Body2, SubBody, Caption } from "Cobalt/BodyText";
import Button from "Cobalt/Button";
import { Subtitle } from "Cobalt/Header";
import { Popover } from "Cobalt/Popover";
import { Card, CardSection } from "Common/Card/";
import { CarrierLogoFlexbox } from "Common/CarrierLogo";
import { getCart } from "Common/CartService";
import { Col, Row } from "Common/Grid";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
import { getBreakpoint } from "Common/Responsive";
import SIOverlayTrigger from "Common/SIOverlayTrigger";

import makeBem from "Utils/makeBem";

const bem = makeBem("DisabilityPlanCard");

const DisabilityPlanCard = ({ plan, selectedPlanID, addDisabilityPlanHandler }) => {
  const { short_term_disability: shortTermPlanId, long_term_disability: longTermPlanId } = useSelector(getCart);

  const plansInCart = [shortTermPlanId, longTermPlanId];

  return (
    <Card mx={4}>
      <CardSection p={0}>
        <Row pb={3} className={bem("planCardHeader")}>
          <Col sm={2} className={bem("planCardHeaderLogo")}>
            <CarrierLogoFlexbox plan={plan} maxSize={3} />
          </Col>
          <Col sm={8} className={bem("planCardHeaderName")}>
            <Subtitle size="large" weight="bold" role="heading" mb={0}>
              {simplifyDisabilityPlanName(plan)}
            </Subtitle>
          </Col>
          <Col sm={2} pr={3} className={bem("planCardHeaderPrice")}>
            <Caption color="muted">
              Estimated price
              <SIOverlayTrigger
                placement="top"
                overlay={
                  <Popover showBackdrop={false} id="more-reasons">
                    Final rates will be determined 24-48 hours after submission
                  </Popover>
                }
              >
                <Icon icon="question-circle" ml={2} />
              </SIOverlayTrigger>
            </Caption>
            <Body1>
              <strong>{formatCurrency(plan?.average_monthly_premium)}</strong>
              /mo
            </Body1>
            <SubBody componentClass="span" weight="bold">
              per employee
            </SubBody>
          </Col>
        </Row>
      </CardSection>
      <CardSection p={0}>
        <Row pt={4} className={bem("bodyContainer")}>
          <Col xs={12} sm={8} mb={2}>
            <Div mt={isIntuit() ? 1 : 2} className={bem("benefitContainer")}>
              <div className={bem("benefit")}>
                <Body2 className={bem("benefitTerm")}>Percentage of salaries covered</Body2>
                <Body2 className={bem("benefitValue")}>{plan.salary_coverage_percentage}%</Body2>
              </div>
              <div className={bem("benefit")}>
                <Body2 className={bem("benefitTerm")}>Maximum weekly benefit</Body2>
                <Body2 className={bem("benefitValue")}>{plan.max_benefit.split(" ")[0]}</Body2>
              </div>
              <div className={bem("benefit")}>
                <Body2 className={bem("benefitTerm")}>Benefit duration</Body2>
                <Body2 className={bem("benefitValue")}>{plan.benefit_duration}</Body2>
              </div>
              <div className={bem("benefit")}>
                <Body2 className={bem("benefitTerm")}>Elimination period</Body2>
                <Body2 className={bem("benefitValue")}>{plan.elimination_period}</Body2>
              </div>
            </Div>
          </Col>
          <Col xs={12} sm={4} style={{ textAlign: "right" }}>
            <div className="buttonContainer">
              {selectedPlanID === plan?.id || plansInCart.includes(plan?.id) ? (
                <Button
                  size="medium"
                  onClick={() => {
                    addDisabilityPlanHandler(plan);
                  }}
                  color="secondary"
                  {...(getBreakpoint() === "xs" ? { fullWidth: true } : {})}
                >
                  <Icon icon="check" pr={2} />
                  Selected
                </Button>
              ) : (
                <Button
                  size="medium"
                  onClick={() => {
                    addDisabilityPlanHandler(plan);
                  }}
                  color="gray"
                  {...(getBreakpoint() === "xs" ? { fullWidth: true } : {})}
                >
                  Select
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </CardSection>
    </Card>
  );
};

export default DisabilityPlanCard;
